// src/features/equipment/components/maintenance/ServiceProviderField.jsx
import React, { useEffect, useState } from 'react';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';

import { contactsAPI } from '../../../contacts/services/contacts';
import { usePermissions } from '../../../auth/hooks/usePermissions';

/**
 * Free-text technician / service company field that suggests the department's
 * contacts. The value stays a plain string, so anyone not in Contacts can
 * still be typed in. Without `contact:read` it's an ordinary text field.
 * `onChange(text, contact)` gets the picked contact, or null once the text is
 * typed or edited by hand.
 */
export default function ServiceProviderField({ label, value, onChange, disabled, autoFocus }) {
  const permissions = usePermissions();
  const canReadContacts = permissions.hasScope('contact:read');
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (!canReadContacts) return undefined;
    let cancelled = false;
    contactsAPI.list()
      .then((response) => {
        if (!cancelled) setContacts(response.data?.data?.contacts || []);
      })
      // Suggestions are optional; typing still works without them
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canReadContacts]);

  return (
    <Autocomplete
      freeSolo
      options={contacts}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        if (!query) return options;
        return options.filter(c => [c.name, c.att, c.role].some(text => text?.toLowerCase().includes(query)));
      }}
      renderOption={({ key, ...props }, option) => (
        <Box component="li" key={option.contact_id} {...props}>
          <Box minWidth={0}>
            <Typography variant="body2" noWrap>{option.name}</Typography>
            {option.role && (
              <Typography variant="caption" color="text.secondary" noWrap display="block">{option.role}</Typography>
            )}
          </Box>
        </Box>
      )}
      inputValue={value ?? ''}
      // 'reset' is the picked option's name, which onChange below already sends
      onInputChange={(event, text, reason) => {
        if (reason === 'input' || reason === 'clear') onChange(text, null);
      }}
      onChange={(event, option) => {
        if (option && typeof option === 'object') onChange(option.name, option);
      }}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          autoFocus={autoFocus}
          slotProps={{ htmlInput: { ...params.inputProps, maxLength: 255 } }}
        />
      )}
    />
  );
}
